import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { theme } from '../theme';
import { feeParen } from '../utils/fee';
import { LogoCompact, PasswordVisibilityToggle } from '../Icons';
import AccountScreenBanner from './AccountScreenBanner';
import { getFreezes, deleteAccount } from '../api/atlast';

// DeleteAccountScreen — new (Sep 7, per Ali's report — Phase 2 of the
// account-settings menu, the highest-stakes item in it).
//
// Password re-verification happens via a real signInWithPassword()
// call, not a separate "check my password" endpoint — Supabase Auth
// doesn't expose one distinct from actually signing in, so re-auth IS
// the verification step here, same reasoning as the backend route's
// own docstring. This does briefly issue a fresh session, which is
// harmless — the account is about to be soft-deleted regardless.
//
// The active-freeze warning is fetched and shown BEFORE any
// password/confirm UI even renders, per Ali's explicit call that this
// forfeiture has to be stated in plain language up front, not buried
// in fine print under a generic "are you sure."
function DeleteAccountScreen() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [activeFreezes, setActiveFreezes] = useState([]);
  const [checkingFreezes, setCheckingFreezes] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    supabase.auth.getUser().then(async ({ data }) => {
      if (!data?.user?.id) return;
      setUserId(data.user.id);
      setUserEmail(data.user.email || '');
      try {
        const result = await getFreezes(data.user.id);
        setActiveFreezes((result.freezes || []).filter(f => f.status === 'active'));
      } catch (e) {
        // Fails open on the warning itself (still lets deletion
        // proceed) — the backend's own force-cancel logic is the real
        // enforcement, this is only the heads-up. A failed GET here
        // shouldn't block someone from deleting their account.
      }
      setCheckingFreezes(false);
    });
  }, []);

  const handleDelete = async () => {
    setError(null);
    if (!password) {
      setError('Enter your password to confirm.');
      return;
    }

    setSubmitting(true);
    const { error: authError } = await supabase.auth.signInWithPassword({
      email: userEmail,
      password
    });
    if (authError) {
      setSubmitting(false);
      setError('Incorrect password.');
      return;
    }

    try {
      await deleteAccount(userId);
      await supabase.auth.signOut();
      navigate('/login', { replace: true });
    } catch (e) {
      setSubmitting(false);
      setError(e?.response?.data?.detail || 'Could not delete your account. Try again.');
    }
  };

  // Total of the fees on the active watches, or '' if any fee is unknown
  // (never guess an amount).
  const feeAmounts = activeFreezes.map(f => f.fee_amount_usd);
  const feeTotalText = feeAmounts.length > 0 && feeAmounts.every(v => v != null && Number.isFinite(Number(v)))
    ? feeParen(feeAmounts.reduce((sum, v) => sum + Number(v), 0)).trim()
    : '';

  return (
    <>
      <AccountScreenBanner section="Delete Account" />
      <div className="login-form-panel" style={{ minHeight: 'calc(100vh - 60px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="login-card yedge">
          <div className="login-card-body">
          <div className="login-card-icon">
            <LogoCompact width={150} color="#1E3A5F" />
          </div>
          <h2 style={{ textAlign: 'center', margin: '0 0 4px', color: theme.textPrimary, fontFamily: theme.fontDisplay }}>
            Delete your account
          </h2>
          <p style={{ textAlign: 'center', color: theme.textMuted, margin: '0 0 20px' }}>
            This can't be undone once the 30-day window closes.
          </p>

          {checkingFreezes ? (
            <p style={{ textAlign: 'center', color: theme.textMuted, fontSize: '13px' }}>Checking your account…</p>
          ) : (
            <>
              {activeFreezes.length > 0 && (
                <div style={{
                  background: 'rgba(178,59,59,0.08)', border: `1px solid ${theme.danger}`, borderRadius: '0px',
                  padding: '14px 16px', marginBottom: '18px'
                }}>
                  <p style={{ fontSize: '13px', fontWeight: 800, color: theme.danger, margin: '0 0 6px' }}>
                    You have {activeFreezes.length} active Price Watch{activeFreezes.length > 1 ? 'es' : ''}.
                  </p>
                  <p style={{ fontSize: '12.5px', color: theme.danger, margin: 0, lineHeight: 1.5 }}>
                    Deleting your account ends {activeFreezes.length > 1 ? 'them' : 'it'} now and settles {activeFreezes.length > 1 ? 'each' : 'it'} on the current price, the same way as when a watch finishes. Depending on that price, the fee{feeTotalText ? ` ${feeTotalText}` : ''} is either <b>charged</b> or <b>not charged</b>. A free trial costs nothing. A watch can't be resumed later.
                  </p>
                </div>
              )}

              <ul style={{ fontSize: '12.5px', color: theme.textMuted, lineHeight: 1.7, margin: '0 0 20px', paddingLeft: '18px' }}>
                <li>Your account is deactivated immediately — you'll be signed out and can't log back in.</li>
                <li>Your data is kept for 30 days in case this was a mistake, then permanently deleted.</li>
                <li><b>Signing up again with the same email won't work during those 30 days either</b> — it's still registered until the 30 days pass.</li>
                <li>Contact support within that window if you want it back — we can restore it in one step.</li>
              </ul>

              <label>Enter your password to confirm</label>
              <div className="login-input-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <PasswordVisibilityToggle visible={showPassword} onClick={() => setShowPassword(v => !v)} />
              </div>

              {error && (
                <p style={{ fontSize: '12.5px', color: theme.danger, marginTop: '8px', marginBottom: 0, fontWeight: 600 }}>{error}</p>
              )}

              <button
                className="login-submit-btn"
                style={{
                  marginTop: '20px', width: '100%', background: theme.danger, color: '#fff',
                  border: 'none', borderRadius: '0px', padding: '13px', fontWeight: 800, fontSize: '14px',
                  cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1
                }}
                onClick={handleDelete}
                disabled={submitting}
              >
                {submitting ? 'Deleting…' : 'Permanently delete my account'}
              </button>
              <p
                onClick={() => navigate('/dashboard')}
                style={{ textAlign: 'center', fontSize: '12.5px', color: theme.textMuted, marginTop: '16px', cursor: 'pointer' }}
              >
                Cancel, keep my account
              </p>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default DeleteAccountScreen;